import { useEffect, useMemo, useState } from "react";
import { InterviewType } from "../../graphql/generated/graphql";
import type { ScheduleConfig } from "../../hooks";
import { useScheduleMutations } from "../../hooks";
import { CloseIcon } from "../Icons";
import { MailParameterReference } from "./MailParameterReference";
import { MailPreview } from "./MailPreview";
import { MailTemplateEditor } from "./MailTemplateEditor";
import { buildScheduleMailPreviewValues } from "./scheduleMailPreview";
import {
  SCHEDULE_MAIL_DEFAULTS,
  SCHEDULE_MAIL_USE_CASE_LABELS,
  type ScheduleMailUseCase,
  scheduleMailParametersFor,
  scheduleMailUseCasesFor,
} from "./scheduleMailTemplate";

interface ScheduleConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termId: string | null;
  // ターム作成時に選んだ所要時間。日程調整設定が未作成のときの初期値に使う
  termDurationMinutes: number | null;
  scheduleConfig: ScheduleConfig | null;
  /** 選べるユースケースと既定値が種別で変わる */
  interviewType: InterviewType;
  /** プレビューで @system_company_name を実値にする。取得前は null */
  companyName?: string | null;
  /** プレビューで @system_interview_title を実値にする。取得前は null */
  interviewTitle?: string | null;
  onSaved: () => void;
}

function toUseCase(
  value: string | null | undefined,
  interviewType: InterviewType,
): ScheduleMailUseCase {
  const selectable = scheduleMailUseCasesFor(interviewType);
  const found = selectable.find((useCase) => useCase === value);
  return found ?? selectable[0];
}

function buildFormState(
  scheduleConfig: ScheduleConfig | null,
  termDurationMinutes: number | null,
  interviewType: InterviewType,
) {
  const useCase = toUseCase(scheduleConfig?.useCase, interviewType);
  const defaults = SCHEDULE_MAIL_DEFAULTS[useCase];
  return {
    useCase,
    durationMinutes:
      scheduleConfig?.durationMinutes ?? termDurationMinutes ?? 30,
    workingHoursStart: scheduleConfig?.workingHoursStart ?? "09:00",
    workingHoursEnd: scheduleConfig?.workingHoursEnd ?? "18:00",
    deadlineDate: (scheduleConfig?.deadlineDate ?? "") as string | undefined,
    reminderEnabled: scheduleConfig?.reminderEnabled ?? false,
    reminderOneWeekBefore: scheduleConfig?.reminderOneWeekBefore ?? false,
    reminderOneDayBefore: scheduleConfig?.reminderOneDayBefore ?? false,
    scheduleTitle: scheduleConfig?.scheduleTitle ?? defaults.title,
    scheduleDescription:
      scheduleConfig?.scheduleDescription ?? defaults.description,
    availableFromDays: scheduleConfig?.availableFromDays ?? 1,
    availableUntilDays: scheduleConfig?.availableUntilDays ?? 30,
    slotDisplayStartDate: (scheduleConfig?.slotDisplayStartDate ?? "") as
      | string
      | undefined,
    slotDisplayEndDate: (scheduleConfig?.slotDisplayEndDate ?? "") as
      | string
      | undefined,
    replyToEmail: (scheduleConfig?.replyToEmail ?? "") as string | undefined,
  };
}

type ConfigFormState = ReturnType<typeof buildFormState>;

function ParameterChipHint({ tokens }: { tokens: string[] }) {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 text-[var(--color-text-secondary)] text-xs">
      {tokens.map((token) => (
        // エディタ内チップと同じ見た目を共通クラスで再利用する
        <span key={token} className="mail-parameter-chip">
          @{token}
        </span>
      ))}
      <span>に入ります</span>
    </div>
  );
}

export function ScheduleConfigDialog({
  open,
  onOpenChange,
  termId,
  termDurationMinutes,
  scheduleConfig,
  interviewType,
  companyName = null,
  interviewTitle = null,
  onSaved,
}: ScheduleConfigDialogProps) {
  const { createScheduleConfig, updateScheduleConfig } = useScheduleMutations();

  const [configForm, setConfigForm] = useState(() =>
    buildFormState(scheduleConfig, termDurationMinutes, interviewType),
  );

  const previewValues = useMemo(
    () =>
      buildScheduleMailPreviewValues({
        useCase: configForm.useCase,
        companyName,
        interviewTitle,
        durationMinutes: configForm.durationMinutes,
        startDate: configForm.slotDisplayStartDate,
        endDate: configForm.slotDisplayEndDate,
        deadlineDate: configForm.deadlineDate,
      }),
    [
      configForm.useCase,
      configForm.durationMinutes,
      configForm.slotDisplayStartDate,
      configForm.slotDisplayEndDate,
      configForm.deadlineDate,
      companyName,
      interviewTitle,
    ],
  );

  // ダイアログは TermDetail に常時マウントされており、scheduleConfig は非同期取得。
  // 初回 useState の時点で scheduleConfig が null だとフォームがデフォルト固定になり、
  // 既存設定のある term で「編集」を開いた際にデフォルトのまま更新→上書きが起きる。
  // open のたびに最新の scheduleConfig から再シードする。
  useEffect(() => {
    if (open) {
      setConfigForm(
        buildFormState(scheduleConfig, termDurationMinutes, interviewType),
      );
    }
  }, [open, scheduleConfig, termDurationMinutes, interviewType]);

  const selectableUseCases = scheduleMailUseCasesFor(interviewType);
  // 呼び方はこの画面の他の文言に揃える（従業員インタビューは候補者、ステークホルダーは回答者）
  const reminderHint =
    interviewType === InterviewType.Stakeholder
      ? "チェックを入れると、予約が済んだ回答者に Zoom の入室情報を改めてお送りします。まだ予約していない回答者には、依頼メールを送った2日後と6日後に予約のお願いを自動でお送りします。"
      : "チェックを入れると、予約が済んだ候補者に Zoom の入室情報を改めてお送りします。まだ予約していない候補者には、「回答期限日」を入れると予約のお願いを自動でお送りします。";

  if (!open) return null;

  // ユースケース切替時、編集済み（現ユースケースのデフォルトと異なる）なら確認してから
  // 切替先のデフォルト文言にリセットする。未編集ならそのままリセット。
  const handleUseCaseChange = (nextUseCase: ScheduleMailUseCase) => {
    if (nextUseCase === configForm.useCase) return;
    const currentDefaults = SCHEDULE_MAIL_DEFAULTS[configForm.useCase];
    const isEdited =
      configForm.scheduleTitle !== currentDefaults.title ||
      configForm.scheduleDescription !== currentDefaults.description;
    if (
      isEdited &&
      !window.confirm(
        "メール文言を切り替え先のデフォルト文言にリセットします。編集中の内容は失われます。よろしいですか？",
      )
    ) {
      return;
    }
    const nextDefaults = SCHEDULE_MAIL_DEFAULTS[nextUseCase];
    setConfigForm({
      ...configForm,
      useCase: nextUseCase,
      scheduleTitle: nextDefaults.title,
      scheduleDescription: nextDefaults.description,
    });
  };

  const buildPayload = (form: ConfigFormState) => ({
    durationMinutes: form.durationMinutes,
    workingHoursStart: form.workingHoursStart,
    workingHoursEnd: form.workingHoursEnd,
    scheduleTitle: form.scheduleTitle,
    scheduleDescription: form.scheduleDescription,
    deadlineDate: form.deadlineDate || null,
    reminderEnabled: form.reminderEnabled,
    reminderOneWeekBefore: form.reminderOneWeekBefore,
    reminderOneDayBefore: form.reminderOneDayBefore,
    availableFromDays: form.availableFromDays,
    availableUntilDays: form.availableUntilDays,
    slotDisplayStartDate: form.slotDisplayStartDate || null,
    slotDisplayEndDate: form.slotDisplayEndDate || null,
    replyToEmail: form.replyToEmail || null,
    useCase: form.useCase,
  });

  const handleConfigUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!configForm.scheduleTitle.trim()) {
      alert("件名を入力してください");
      return;
    }
    const isCreate = !scheduleConfig?.scheduleConfigId;
    if (isCreate && !termId) return;
    try {
      const result = isCreate
        ? await createScheduleConfig({
            termId: termId as string,
            ...buildPayload(configForm),
          })
        : await updateScheduleConfig({
            scheduleConfigId: scheduleConfig?.scheduleConfigId as string,
            ...buildPayload(configForm),
          });
      if (result) {
        onOpenChange(false);
        onSaved();
        alert(
          isCreate
            ? "日程調整設定を作成しました"
            : "日程調整設定を更新しました",
        );
      } else {
        alert(isCreate ? "作成に失敗しました" : "更新に失敗しました");
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "保存に失敗しました");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="max-h-[90vh] w-[90%] max-w-[600px] overflow-auto rounded-xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-bold text-lg">日程調整設定を編集</h3>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer border-none bg-none p-1"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleConfigUpdate} className="flex flex-col gap-4">
          {/* ユースケース。選べる種類が1つのときは選択させる意味がないので欄ごと出さない */}
          {selectableUseCases.length > 1 && (
            <>
              <div>
                <label className="mb-1 block font-medium text-sm">
                  ユースケース
                </label>
                <div className="inline-flex overflow-hidden rounded-lg border border-[var(--color-border-default)]">
                  {selectableUseCases.map((useCase) => (
                    <button
                      key={useCase}
                      type="button"
                      aria-pressed={configForm.useCase === useCase}
                      onClick={() => handleUseCaseChange(useCase)}
                      className={`px-7 py-2 font-medium text-sm transition-colors ${
                        configForm.useCase === useCase
                          ? "bg-[var(--color-primary-500)] text-white"
                          : "bg-white text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle-gray)]"
                      }`}
                    >
                      {SCHEDULE_MAIL_USE_CASE_LABELS[useCase]}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-[var(--color-text-secondary)] text-xs">
                  切り替えると、下のメール文言がそのユースケースのデフォルト文言にリセットされます
                </p>
              </div>

              <hr className="border-[var(--color-border-default)]" />
            </>
          )}

          {/* メールに差し込まれる設定値 */}
          <p className="font-bold text-[var(--color-primary-500)] text-xs tracking-wide">
            メールに差し込まれる設定値
          </p>
          <div>
            <label className="mb-1 block font-medium text-sm">
              所要時間（分）
            </label>
            <input
              type="number"
              value={configForm.durationMinutes}
              onChange={(e) =>
                setConfigForm({
                  ...configForm,
                  durationMinutes: Number.parseInt(e.target.value, 10),
                })
              }
              required
              className="w-32 rounded border border-[var(--color-border-default)] p-2"
            />
            <ParameterChipHint tokens={["interview_time"]} />
          </div>
          <div>
            <label className="mb-1 block font-medium text-sm">実施時期</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={configForm.slotDisplayStartDate || ""}
                onChange={(e) =>
                  setConfigForm({
                    ...configForm,
                    slotDisplayStartDate: e.target.value || undefined,
                  })
                }
                className="rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm"
              />
              <span className="text-[var(--color-text-secondary)] text-sm">
                〜
              </span>
              <input
                type="date"
                value={configForm.slotDisplayEndDate || ""}
                onChange={(e) =>
                  setConfigForm({
                    ...configForm,
                    slotDisplayEndDate: e.target.value || undefined,
                  })
                }
                className="rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm"
              />
            </div>
            <ParameterChipHint tokens={["start_date", "end_date"]} />
          </div>
          <div>
            <label className="mb-1 block font-medium text-sm">
              回答期限日（空欄で期限なし）
            </label>
            <input
              type="date"
              value={configForm.deadlineDate || ""}
              onChange={(e) =>
                setConfigForm({
                  ...configForm,
                  deadlineDate: e.target.value || undefined,
                })
              }
              className="rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm"
            />
            <ParameterChipHint tokens={["reservation_deadline"]} />
          </div>

          <hr className="border-[var(--color-border-default)]" />

          {/* 宛先の呼び方は種別で変わる。ステークホルダーインタビューの宛先はクライアントではなく、
              クライアントについて語る上司・同僚にあたる */}
          <p className="font-bold text-[var(--color-primary-500)] text-xs tracking-wide">
            {interviewType === InterviewType.Stakeholder
              ? "回答者向け 日程調整メール"
              : "クライアント向け 日程調整メール"}
          </p>
          <div>
            <label className="mb-1 block font-medium text-sm">件名</label>
            <MailTemplateEditor
              singleLine
              parameters={scheduleMailParametersFor(configForm.useCase)}
              value={configForm.scheduleTitle}
              onChange={(text) =>
                setConfigForm((prev) => ({ ...prev, scheduleTitle: text }))
              }
            />
          </div>
          <div>
            <label className="mb-1 block font-medium text-sm">本文</label>
            <MailTemplateEditor
              parameters={scheduleMailParametersFor(configForm.useCase)}
              value={configForm.scheduleDescription}
              onChange={(text) =>
                setConfigForm((prev) => ({
                  ...prev,
                  scheduleDescription: text,
                }))
              }
            />
            <p className="mt-1 text-[var(--color-primary-500)] text-xs">
              <span className="font-mono">@</span>{" "}
              を入力するとパラメータを挿入できます（値は送信時に自動で置き換わります）
            </p>
            <MailPreview
              subject={configForm.scheduleTitle}
              body={configForm.scheduleDescription}
              values={previewValues}
              parameters={scheduleMailParametersFor(configForm.useCase)}
            />
            <MailParameterReference
              parameters={scheduleMailParametersFor(configForm.useCase)}
            />
          </div>

          <hr className="border-[var(--color-border-default)]" />

          {/* その他の設定 */}
          <details className="rounded-md border border-[var(--color-border-default)] p-3">
            <summary className="cursor-pointer font-medium text-sm">
              その他の設定（勤務時間・実施前リマインド・日程枠の有効期間・Reply-To）
            </summary>
            <div className="mt-4 flex flex-col gap-4">
              <div className="flex gap-4">
                <div>
                  <label className="mb-1 block font-medium text-sm">
                    勤務開始時間
                  </label>
                  <input
                    type="time"
                    value={configForm.workingHoursStart}
                    onChange={(e) =>
                      setConfigForm({
                        ...configForm,
                        workingHoursStart: e.target.value,
                      })
                    }
                    className="rounded border border-[var(--color-border-default)] p-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block font-medium text-sm">
                    勤務終了時間
                  </label>
                  <input
                    type="time"
                    value={configForm.workingHoursEnd}
                    onChange={(e) =>
                      setConfigForm({
                        ...configForm,
                        workingHoursEnd: e.target.value,
                      })
                    }
                    className="rounded border border-[var(--color-border-default)] p-2"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={configForm.reminderEnabled}
                    onChange={(e) =>
                      setConfigForm({
                        ...configForm,
                        reminderEnabled: e.target.checked,
                      })
                    }
                    className="mr-2"
                  />
                  実施前リマインドを送る
                </label>
                {/* 「リマインド」だけでは、まだ予約していない人へ予約をお願いするメールの設定と読める。
                    このチェックボックスが動かすのは予約済みの人への当日案内だけなので、送る相手と中身を書く。
                    まだ予約していない人へのリマインドは種別で発火条件が違う（従業員は回答期限日を起点、
                    ステークホルダーは依頼を送った日を起点）ので、書き分ける */}
                <p className="text-[var(--color-text-secondary)] text-xs">
                  {reminderHint}
                </p>
                {configForm.reminderEnabled && (
                  <div className="pl-6">
                    <label className="mb-2 flex items-center">
                      <input
                        type="checkbox"
                        checked={configForm.reminderOneWeekBefore}
                        onChange={(e) =>
                          setConfigForm({
                            ...configForm,
                            reminderOneWeekBefore: e.target.checked,
                          })
                        }
                        className="mr-2"
                      />
                      実施日の1週間前
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={configForm.reminderOneDayBefore}
                        onChange={(e) =>
                          setConfigForm({
                            ...configForm,
                            reminderOneDayBefore: e.target.checked,
                          })
                        }
                        className="mr-2"
                      />
                      実施日の前日
                    </label>
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1.5 block font-medium text-[var(--color-text-primary)] text-sm">
                  日程調整枠の有効期間（相対日数）
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={configForm.availableFromDays}
                    onChange={(e) =>
                      setConfigForm({
                        ...configForm,
                        availableFromDays:
                          Number.parseInt(e.target.value, 10) || 0,
                      })
                    }
                    className="w-20 rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm"
                  />
                  <span className="text-[var(--color-text-secondary)] text-sm">
                    日後 〜
                  </span>
                  <input
                    type="number"
                    min="1"
                    value={configForm.availableUntilDays}
                    onChange={(e) =>
                      setConfigForm({
                        ...configForm,
                        availableUntilDays:
                          Number.parseInt(e.target.value, 10) || 1,
                      })
                    }
                    className="w-20 rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm"
                  />
                  <span className="text-[var(--color-text-secondary)] text-sm">
                    日後
                  </span>
                </div>
              </div>
              <div>
                <label className="mb-1 block font-medium text-sm">
                  メール返信先アドレス (Reply-To)
                </label>
                <input
                  type="email"
                  value={configForm.replyToEmail || ""}
                  onChange={(e) =>
                    setConfigForm({
                      ...configForm,
                      replyToEmail: e.target.value || undefined,
                    })
                  }
                  placeholder="reply-to@example.com"
                  className="w-full rounded-md border border-[var(--color-border-default)] px-3 py-2 text-sm"
                />
                <p className="mt-1 text-[var(--color-text-secondary)] text-xs">
                  設定すると、候補者への各種メールの返信先がこのアドレスになります
                </p>
              </div>
            </div>
          </details>

          <button
            type="submit"
            className="cursor-pointer rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2.5 font-medium text-sm text-white"
          >
            更新
          </button>
        </form>
      </div>
    </div>
  );
}
